import React, { useEffect, useState, useMemo } from "react";
import { styled } from "@mui/material/styles";
import CategoryDropdowns from "./CategoryDropdowns";
import MockupCard from "../../components/MockupCard";
import InfiniteScroll from "../../components/InfiniteScroll";
import apiClient from "../../api/apiClient";
import LoadingSpinner from "../../components/LoadingSpinner";
import FiltersPanel from "../../components/FiltersPanel";
import BundleListingDetails from "../../Dashboard-User/BundleDetails";
import NotFound from "../../components/NotFound";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { toggledFilterArray } from "../../utils/filterSelection";
import { SearchBar } from "../../Dashboard-Seller/MyListings/components/SearchBar";
import { useIndexPageData } from "../../contextapi/Indexpagedata";
import { buildCategoryBreadcrumb } from "./categoryBreadcrumb";

const CategoryTitle = styled("div")({
  color: "#000 !important",
  fontSize: "65px !important",
  lineHeight: "1.2 !important",
  whiteSpace: "nowrap !important",
  fontFamily: "Helvetica Bold, sans-serif !important",
  fontWeight: "bold !important",
  margin: "0 0 1rem 0 !important",
  display: "block !important",
  textTransform: "none !important",

  "@media (max-width: 1200px)": {
    fontSize: "50px !important",
  },
  "@media (max-width: 992px)": {
    fontSize: "40px !important",
  },
  "@media (max-width: 768px)": {
    fontSize: "32px !important",
  },
  "@media (max-width: 576px)": {
    fontSize: "26px !important",
  },
});

// const mockpupData = [
//     {
//         imageUrl: '../assets/images/mockup-pic1.png',
//         title: 'Mockup name',
//         description: 'Card Description',
//         price: '100',
//         seller: 'John Doe'
//     },
//     {
//         imageUrl: '../assets/images/mockup-pic1.png',
//         title: 'Mockup name',
//         description: 'Card Description',
//         price: '100',
//         seller: 'Johns Shop'
//     },
//     {
//         imageUrl: '../assets/images/mockup-pic1.png',
//         title: 'Mockup name',
//         description: 'Card Description',
//         price: '100',
//         seller: 'John mockupShop'
//     }
// ];

const Categories = () => {
  const { categories, nestedSubCategories } = useIndexPageData();
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(null);
  const [loader, setloader] = useState(false);

  const [filtersChanged, setFiltersChanged] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [subCategoryId, setsubCategoryId] = useState(null);

  const [subSubCategoryId, setSubSubCategoryId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [selectedSubSubCategoryId, setSelectedSubSubCategoryId] = useState(null);
  const [printingProviders, setPrintingProviders] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showBundleListings, setShowBundleListings] = useState(false);
  const [bundleListingsById, setBundleListingsById] = useState(false);
  const [bundleListingsId, setBundleListingsId] = useState(false);

  const defaultTypes = ["AI", "Real", "3D", "Mix"];
  const defaultPrice = ["Free", "Paid"];
  const defaultSizes = ["Small", "Medium", "Large"];
  const defaultAmount = ["Single", "Bundle"];

  const [draftAmount, setDraftAmount] = useState(defaultAmount);
  const [draftTypes, setDraftTypes] = useState(defaultTypes);
  const [draftProviders, setDraftProviders] = useState([]);
  const [draftPrice, setDraftPrice] = useState(defaultPrice);
  const [draftSizes, setDraftSizes] = useState(defaultSizes);

  const [appliedAmount, setAppliedAmount] = useState(defaultAmount);
  const [appliedTypes, setAppliedTypes] = useState(defaultTypes);
  const [appliedProviders, setAppliedProviders] = useState([]);
  const [appliedPrice, setAppliedPrice] = useState(defaultPrice);
  const [appliedSizes, setAppliedSizes] = useState(defaultSizes);

  // For base stuff like title, category etc.
  const buildBaseQueryParams = () => {
    const baseParams = new URLSearchParams();

    if (appliedSearch) baseParams.append("title", appliedSearch);
    if (categoryId) baseParams.append("category", categoryId);
    if (subCategoryId) baseParams.append("subcategory", subCategoryId);

    if (subSubCategoryId) baseParams.append("subsubcategory", subSubCategoryId);

    return baseParams;
  };

  // For filters like type, price, provider, size
  const buildFilterQuery = () => {
    const query = new URLSearchParams();

    if (appliedTypes.length > 0) {
      appliedTypes.forEach((type) => query.append("type", type));
    }

    if (appliedProviders.length > 0) {
      appliedProviders.forEach((provider) =>
        query.append("provider", provider)
      );
    }

    if (appliedPrice.length === 1) {
      query.append("price", appliedPrice.includes("Free") ? "free" : "price");
    }

    if (appliedSizes.length > 0) {
      appliedSizes.forEach((size) => query.append("size", size.toLowerCase()));
    }

    return query;
  };

  // Combined fetch function for infinite scroll
  const fetchCombinedData = useMemo(() => {
    return async (page) => {
      const baseParams = buildBaseQueryParams();
      const filterQuery = buildFilterQuery();
      const amounts = appliedAmount.map((t) => t.toLowerCase());
      
      const shouldFetchSingle = amounts.includes("single");
      const shouldFetchBundle = amounts.includes("bundle");

      const promises = [];
      if (shouldFetchSingle) {
        let finalURL = `/listings/?page=${page}&page_size=12&${baseParams.toString()}`;
        finalURL += `&${filterQuery.toString()}`;
        promises.push(apiClient.get(finalURL));
      }
      
      if (shouldFetchBundle) {
        let finalURL = `/bundle-listings/?page=${page}&calledFrom=home&page_size=12&${baseParams.toString()}`;
        finalURL += `&${filterQuery.toString()}`;
        promises.push(apiClient.get(finalURL));
      }

      if (promises.length === 0) {
        return { ok: false, data: { results: [], count: 0 } };
      }

      const results = await Promise.all(promises);
      const mockupsResult = shouldFetchSingle ? results[0] : null;
      const bundlesResult = shouldFetchBundle ? (shouldFetchSingle ? results[1] : results[0]) : null;

      const mockups = mockupsResult?.ok ? mockupsResult.data.results || [] : [];
      const bundles = bundlesResult?.data?.results || [];

      // Combine and sort by created_at
      const combined = [...bundles, ...mockups].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      const count = mockupsResult?.data?.count || bundlesResult?.data?.count || 0;

      return {
        ok: mockupsResult?.ok || bundlesResult?.ok,
        data: {
          results: combined,
          count: count
        }
      };
    };
  }, [appliedSearch, categoryId, subCategoryId, subSubCategoryId, appliedTypes, appliedProviders, appliedPrice, appliedSizes, appliedAmount]);

  // Use infinite scroll hook
  const { data: records, loading, hasMore, loadMore, refetch } = useInfiniteScroll(
    fetchCombinedData,
    [appliedSearch, categoryId, subCategoryId, subSubCategoryId, appliedTypes, appliedProviders, appliedPrice, appliedSizes, appliedAmount, filtersChanged],
    12
  );
  const fetchBundleDetail = async () => {
    try {
      const result = await apiClient.get(
        `/bundle-listings/${bundleListingsId}/`
      );
      if (!result.ok) return;
      setBundleListingsById(result?.data || {});
      setShowBundleListings(true);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (bundleListingsId) {
      fetchBundleDetail();
    }
  }, [bundleListingsId]);

  const fetchPrintProviders = async () => {
    const result = await apiClient.get(
      `/print-providers/?products_not_required=1`
    );
    if (result.ok) {
      const list = result?.data?.results || [];
      const ids = list.map((item) => item?.id);
      setPrintingProviders(list);
      setAppliedProviders(ids);
      setDraftProviders(ids);
    }
  };

  useEffect(() => {
    fetchPrintProviders();
  }, []);

  const toggleDraftSelection = (value, array, type) => {
    const newArray = toggledFilterArray(value, array);
    if (newArray === null) return;

    switch (type) {
      case "type":
        setDraftTypes(newArray);
        break;
      case "provider":
        setDraftProviders(newArray);
        break;
      case "price":
        setDraftPrice(newArray);
        break;
      case "size":
        setDraftSizes(newArray);
        break;
      case "amount":
        setDraftAmount(newArray);
        break;
      default:
        break;
    }
  };

  const handleApplyFilters = () => {
    setAppliedTypes([...draftTypes]);
    setAppliedProviders([...draftProviders]);
    setAppliedPrice([...draftPrice]);
    setAppliedSizes([...draftSizes]);
    setAppliedAmount([...draftAmount]);
    setFiltersChanged(true);
  };

  const handleClearFilters = () => {
    const allProviderIds = printingProviders.map((p) => p.id);
    setDraftTypes([...defaultTypes]);
    setDraftPrice([...defaultPrice]);
    setDraftAmount([...defaultAmount]);
    setDraftSizes([...defaultSizes]);
    setDraftProviders(allProviderIds);
    setAppliedTypes([...defaultTypes]);
    setAppliedPrice([...defaultPrice]);
    setAppliedAmount([...defaultAmount]);
    setAppliedSizes([...defaultSizes]);
    setAppliedProviders(allProviderIds);
    setFiltersChanged(true);
  };

  const handleApplySearch = (value) => {
    setAppliedSearch(value || null);
  };

  const categoryPathLabel = useMemo(
    () =>
      buildCategoryBreadcrumb(
        categoryId,
        subCategoryId,
        subSubCategoryId,
        categories,
        nestedSubCategories,
      ),
    [
      categoryId,
      subCategoryId,
      subSubCategoryId,
      categories,
      nestedSubCategories,
    ],
  );

  const showFiltersBar = Boolean(categoryId || records?.length > 0);
  const hasActiveSearch = Boolean(appliedSearch?.trim());
  const showEmptyState =
    categoryId ||
    subCategoryId ||
    subSubCategoryId ||
    hasActiveSearch ||
    filtersChanged;

  const handleCategoryFetch = (override) => {
    if (override && typeof override === "object") {
      const catId = override.categoryId !== undefined ? override.categoryId : selectedCategoryId;
      const subId = override.subCategoryId !== undefined ? override.subCategoryId : selectedSubCategoryId;
      const subSubId = override.subSubCategoryId !== undefined ? override.subSubCategoryId : selectedSubSubCategoryId;
      setSelectedCategoryId(catId);
      setSelectedSubCategoryId(subId);
      setSelectedSubSubCategoryId(subSubId);
      setCategoryId(catId);
      setsubCategoryId(subId);
      setSubSubCategoryId(subSubId);
    } else {
      setCategoryId(selectedCategoryId);
      setsubCategoryId(selectedSubCategoryId);
      setSubSubCategoryId(selectedSubSubCategoryId);
    }
  };

  return (
    <div>
      <section className=" my-5 px-3 ">
        <div
          className="container-lg px-0"
          style={{ backgroundColor: "#FAE2CF", borderBottomLeftRadius: "50px" }}
        >
          <div className="row g-0">
            <div className="col-md-6 offset-md-1 px-3">
              <div className="py-5">
                <CategoryTitle className="mb-3">
                  Browse by category
                </CategoryTitle>
                <SearchBar
                  search={searchInput}
                  setSearch={setSearchInput}
                  appliedSearch={appliedSearch || ""}
                  onApply={handleApplySearch}
                  placeholder="Search mockups in selected category"
                />
              </div>
            </div>
            <div className="col-md-5 px-0">
              <div className="h-100 d-flex justify-content-end">
                <img
                  src="/assets/images/categories.svg"
                  alt="Categories"
                  className="img-fluid h-100 mb-hidden object-fit-cover"
                  style={{
                    borderTopLeftRadius: "60px",
                    width: "75%",
                    objectFit: "contain",
                    objectPosition: "right center",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-5 px-3">
        <div className="container-lg">
          <CategoryDropdowns
            selectedCategoryId={selectedCategoryId}
            selectedSubCategoryId={selectedSubCategoryId}
            selectedSubSubCategoryId={selectedSubSubCategoryId}
            onCategoryChange={setSelectedCategoryId}
            onSubCategoryChange={setSelectedSubCategoryId}
            onSubSubCategoryChange={setSelectedSubSubCategoryId}
            onFetch={handleCategoryFetch}
            fetchLoading={loading}
          />

          {showFiltersBar && (
            <FiltersPanel
              expanded={showFilters}
              onToggle={() => setShowFilters((prev) => !prev)}
              breadcrumb={
                categoryPathLabel ? (
                  <span className="filters-panel__path">
                    {categoryPathLabel}
                  </span>
                ) : null
              }
              onClearAll={handleClearFilters}
              selectedTypes={draftTypes}
              selectedProviders={draftProviders}
              selectedPrice={draftPrice}
              selectedSizes={draftSizes}
              selectedAmount={draftAmount}
              toggleSelection={toggleDraftSelection}
              onApplyFilters={handleApplyFilters}
              printingProviders={printingProviders}
            />
          )}
          <div className="row">
            <div className="col-12">
              {!showBundleListings ? (
                <>
                  {/* Add your card list here */}

                  {loading && records?.length === 0 ? (
                    <div className="d-flex justify-content-center mt-5">
                      <LoadingSpinner />
                    </div>
                  ) : records?.length > 0 ? (
                    <InfiniteScroll
                      onLoadMore={loadMore}
                      hasMore={hasMore}
                      loading={loading}
                    >
                      <div className="row">
                        {records?.map((mockup, index) => (
                          <MockupCard
                            key={mockup?.id || index}
                            imageUrl={mockup?.use_main_file_as_thumbnail ? mockup?.compressed_file : mockup?.thumbnails?.[0]?.image}
                            title={mockup.title}
                            description={mockup.short_description}
                            price={mockup.price}
                            seller={mockup.user_name}
                            id={mockup.id}
                            item={mockup}
                            col={3}
                            setBundleListingsById={setBundleListingsById}
                            setShowBundleListings={setShowBundleListings}
                            setBundleListingsId={setBundleListingsId}
                          />
                        ))}
                      </div>
                    </InfiniteScroll>
                  ) : showEmptyState ? (
                    <NotFound hasActiveSearch={hasActiveSearch} />
                  ) : null}
                </>
              ) : (
                <BundleListingDetails
                  bundleListingsById={bundleListingsById}
                  loader={loading}
                  setShowBundleListings={setShowBundleListings}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Categories;
